import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { allNavItems } from '@/app/navigation';
import { AssistantWidget } from '@/features/assistant/AssistantWidget';
import { MaintenanceNotices } from '@/features/status/MaintenanceNotices';
import { TermsOfUseGate } from '@/features/terms/TermsOfUseGate';
import { useAuth } from '@/shared/auth/useAuth';
import { useIdleLock } from '@/shared/auth/useIdleLock';
import { cn } from '@/shared/lib/cn';

import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

const COLLAPSE_KEY = 'seguracore.sidebar.collapsed';

/**
 * A moldura da empresa. As rotas da moldura entram pela saida do roteador; quem monta a
 * moldura fora delas (a pagina nao encontrada do endereco publico, ADR 0095) passa o
 * conteudo como filho.
 */
export function AppShell({ children }: { children?: ReactNode } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAuth();

  // Bloqueio por inatividade, com o tempo que a empresa configurou. Desligado por
  // padrao: o valor do OnPoint nao e conhecido.
  const bloquear = useCallback(() => {
    void navigate('/sessao-bloqueada', { state: { from: location.pathname } });
  }, [navigate, location.pathname]);
  useIdleLock(session?.tenant.access?.session_lock_idle_minutes, bloquear);

  const [collapsed, setCollapsed] = useState(() => {
    // Preferencia de interface pode viver no navegador. Nada aqui e dado de
    // negocio nem credencial.
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsed));
    } catch {
      // Navegacao privativa pode recusar a escrita. A preferencia e opcional.
    }
  }, [collapsed]);

  // A gaveta fecha ao mudar de rota, inclusive quando a navegacao vem dos botoes
  // de historico do navegador. O ajuste acontece durante a renderizacao, e nao em
  // um efeito, para evitar um quadro intermediario com a gaveta ainda aberta
  // sobre a pagina nova.
  const [lastPath, setLastPath] = useState(location.pathname);
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname);
    setMobileOpen(false);
  }

  const current = allNavItems().find((item) => item.path === location.pathname);
  const title = current?.label ?? 'SeguraCore';

  return (
    <div className="flex min-h-dvh">
      <div className="hidden lg:block">
        <div className="sticky top-0 h-dvh">
          <Sidebar
            collapsed={collapsed}
            onToggle={() => setCollapsed((value) => !value)}
          />
        </div>
      </div>

      {mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Fechar menu"
            className="absolute inset-0 bg-ink-900/50 backdrop-blur-[1px]"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0">
            <Sidebar
              collapsed={false}
              onToggle={() => setMobileOpen(false)}
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar title={title} onOpenMenu={() => setMobileOpen(true)} />
        {/* A pagina com quadro usa toda a largura util, e nao a largura maxima do resto
            do sistema: as colunas precisam dela para caber sem rolar. A condicao olha
            o proprio conteudo, e assim vale para o quadro de qualquer tela e para a
            visao de quadro que a pessoa liga dentro da pagina. A lista com muitos dados
            pede o mesmo com `data-tela-larga`. */}
        <main
          className={cn(
            'flex-1 px-4 py-6 sm:px-6 lg:px-8',
            'has-[[data-kanban]]:py-4 lg:has-[[data-kanban]]:px-5',
            'has-[[data-tela-larga]]:py-4 lg:has-[[data-tela-larga]]:px-5',
          )}
        >
          <div className="mx-auto w-full max-w-[1400px] has-[[data-kanban]]:max-w-none has-[[data-tela-larga]]:max-w-none">
            <MaintenanceNotices />
            {/* Termo de uso (P-USU-2): aviso ou bloqueio ate o aceite, conforme a
                empresa configurou. */}
            <TermsOfUseGate />
            {children ?? <Outlet />}
          </div>
        </main>
      </div>

      <AssistantWidget />
    </div>
  );
}
